/**
 * Gestione tenant — pagina admin unica con le sezioni del vecchio pannello Maeway:
 * Tenant · Ruoli · Associazioni R÷P (role-claims) · Permessi CRUD · Rotte ·
 * Ruoli utenti (user-roles) · Associazioni T÷U (user-tenants).
 *
 * Modello framework: i "permessi" sono claim `Entità.verbo` (es. Project.create),
 * le "rotte" sono le entità/risorse, le associazioni sono i join role-claims /
 * user-roles / user-tenants. Le tab con risorsa confermata sono operative; Rotte e
 * Permessi CRUD derivano dal catalogo claim/risorse (vedi nota in pagina).
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ActionIcon, Alert, Anchor, Badge, Button, Card, Collapse, Group, Loader, Menu, Modal, NumberInput, Pagination, ScrollArea,
  SegmentedControl, Select, SimpleGrid, Stack, Switch, Table, Text, TextInput, ThemeIcon, Tooltip, rem,
} from '@mantine/core';
import {
  IconBuilding, IconShieldCheck, IconKey, IconListCheck, IconRoute, IconUsers, IconUserShield, IconArrowRight, IconSearch,
  IconInfoCircle, IconPlus, IconPencil, IconTrash, IconDeviceFloppy, IconLock, IconChecks, IconSquareOff,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { notifyError } from '@/shared/lib/notify';
import { modals } from '@mantine/modals';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { MobileHeader } from '@/shared/mobile/MobileHeader';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { StatCard } from '@/shared/components/StatCard';
import { LeaveApprovalDefaultCard } from '@/features/leave/LeaveApprovalDefaultCard';
import type { TenantChainNode } from '@/features/leave/approvalWorkflow';
import { mw, MW_CARD_SHADOW, MW_CODE_STYLE } from '@/shared/mobile/tokens';
import { useAuth } from '@/app/providers/AuthProvider';
import { useCurrentTenantId } from '@/shared/hooks/useCurrentTenant';
import { fetchOne, fetchList, fetchAllPages, createOne, updateOne, deleteOne } from '@/shared/api/jsonapi/client';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { EmptyState } from '@/shared/components/EmptyState';
import { fetchRoleClaims, addRoleClaim, removeRoleClaim } from '@/features/roles/api';
import { CENSITED_ENTITIES, entityName } from '@/shared/lib/entities';
import { ROUTE_PERMISSIONS } from '@/shared/lib/routePermissions';
// Dal modulo dati, non dal provider: quello importa `@/app/router` e la pagina si
// sarebbe tirata dietro l'intero route tree per un semplice elenco.
import { STATIC_ROUTES } from '@/shared/lib/staticRoutes';
import {
  isRoleAssignableToInternal, isAutoManagedProfile, AUTO_MANAGED_REASON, UNASSIGNABLE_REASON, canAssign, fetchAssignableRoleIds,
  isRoleSelectable, roleLabel, type RoleRow,
} from '@/features/roles/api';
import { UserTenantsManager } from '@/features/tenants/UserTenantsManager';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

/** Riga introduttiva che spiega cosa fa una tab (UX più comprensibile). */
function TabIntro({ children }: { children: ReactNode }) {
  return (
    <Alert variant="light" color="blue" icon={<IconInfoCircle size={18} />} mb="md" radius="md">
      <Text size="sm">{children}</Text>
    </Alert>
  );
}

/** Verbi CRUD ↔ etichetta colonna (italiano). */
const CRUD_VERBS = [
  { verb: 'read', label: 'Lettura', color: 'blue' },
  { verb: 'create', label: 'Creazione', color: 'green' },
  { verb: 'update', label: 'Aggiornamento', color: 'orange' },
  { verb: 'delete', label: 'Cancellazione', color: 'red' },
] as const;

export const Route = createFileRoute('/_app/admin/tenant-management')({ component: TenantManagementPage });

interface Role extends Denormalized { type: 'roles'; name?: string; displayName?: string | null; level?: number; typology?: string; roleType?: string; isFromMaster?: boolean }
interface RoleClaim extends Denormalized { type: 'role-claims'; claimType?: string; claimValue?: string; role?: { id: string; name?: string } | null }
interface UserRole extends Denormalized { type: 'user-roles'; user?: { id: string; email?: string; firstName?: string; lastName?: string } | null; role?: { id: string; name?: string; level?: number; isFromMaster?: boolean } | null }
interface UserRow extends Denormalized { type: 'users'; firstName?: string; lastName?: string; email?: string; userName?: string }
interface TenantRow extends Denormalized {
  type: 'tenants'; name?: string; businessName?: string; vatNumber?: string; enabled?: boolean;
  /** APPROVAL-WORKFLOW-CONFIG (BE v1.76): `manager-only` | `two-level` | null = eredita. */
  leaveDefaultApprovalWorkflow?: string | null;
  /** Incluso nella GET: serve a mostrare cosa si erediterebbe svuotando il campo. */
  parentTenant?: TenantChainNode | null;
}

function TenantManagementPage() {
  const { decoded } = useAuth();
  const tenantId = decoded?.tenantId ?? '';
  const tenantQ = useQuery({
    queryKey: ['tenant-mgmt-current', tenantId],
    enabled: Boolean(tenantId),
    queryFn: () => fetchOne<TenantRow>('tenants', tenantId, { include: ['parentTenant'] }),
  });
  const tenantName = tenantQ.data?.name ?? tenantQ.data?.businessName ?? (tenantId ? `Tenant ${tenantId.slice(0, 8)}` : '—');

  const TABS = [
    { value: 'tenant', label: 'Azienda', icon: IconBuilding },
    { value: 'roles', label: 'Ruoli', icon: IconShieldCheck },
    { value: 'role-perm', label: 'Permessi per ruolo', icon: IconKey },
    { value: 'crud-perm', label: 'Permessi CRUD', icon: IconListCheck },
    { value: 'routes', label: 'Rotte (pagine)', icon: IconRoute },
    { value: 'user-roles', label: 'Ruoli degli utenti', icon: IconUserShield },
    { value: 'user-tenants', label: 'Utenti dell’azienda', icon: IconUsers },
  ] as const;
  const [tab, setTab] = useState<(typeof TABS)[number]['value']>('tenant');

  // KPI reali (page.size:1 → meta.total): ruoli, permessi, utenti del tenant.
  const rolesCountQ = useQuery({ queryKey: ['tm-count-roles'], staleTime: 60_000, queryFn: async () => (await fetchList('roles', { page: { size: 1 } })).total });
  const claimsCountQ = useQuery({ queryKey: ['tm-count-claims'], staleTime: 60_000, queryFn: async () => (await fetchList('role-claims', { page: { size: 1 } })).total });
  const usersCountQ = useQuery({ queryKey: ['tm-count-users', tenantId], enabled: Boolean(tenantId), staleTime: 60_000, queryFn: async () => (await fetchList('user-tenants', { filter: { 'tenant.id': { eq: tenantId } }, page: { size: 1 } })).total });

  const isMobile = useIsMobileShell();

  const kpi = (
    <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
      <StatCard label="Ruoli" icon={IconShieldCheck} color="brand" value={rolesCountQ.data ?? 0} loading={rolesCountQ.isLoading} error={rolesCountQ.isError} />
      <StatCard label="Permessi (claim)" icon={IconKey} color="blue" value={claimsCountQ.data ?? 0} loading={claimsCountQ.isLoading} error={claimsCountQ.isError} />
      <StatCard label="Utenti dell’azienda" icon={IconUsers} color="green" value={usersCountQ.data ?? 0} loading={usersCountQ.isLoading} error={usersCountQ.isError} />
      <StatCard label="Pagine app" icon={IconRoute} color="gray" value={STATIC_ROUTES.length} />
    </SimpleGrid>
  );

  const tabContent = (
    <div>
      {tab === 'tenant' && (
        <Card withBorder radius="lg" padding="lg" style={{ boxShadow: MW_CARD_SHADOW, borderColor: mw.line }}>
          <Group justify="space-between" wrap="wrap" gap="md">
            <Group gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
              <span style={{ width: 48, height: 48, borderRadius: 13, flexShrink: 0, background: mw.accentTint, color: mw.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconBuilding size={26} /></span>
              <div style={{ minWidth: 0 }}>
                <Group gap="xs">
                  <Text fw={700} fz="lg" style={{ color: mw.ink900 }}>{tenantName}</Text>
                  {tenantQ.data && (tenantQ.data.enabled
                    ? <Badge color="green" variant="light">Attivo</Badge>
                    : <Badge color="red" variant="light">Disattivato</Badge>)}
                </Group>
                <Text size="sm" c="dimmed">P.IVA {tenantQ.data?.vatNumber ?? '—'} · ID {tenantId.slice(0, 8)}</Text>
              </div>
            </Group>
            {tenantId && (
              <Button component={Link} to={`/admin/tenants/${tenantId}`} variant="light" rightSection={<IconArrowRight size={16} />}>
                Apri scheda completa
              </Button>
            )}
          </Group>
        </Card>
      )}
      {tab === 'tenant' && (
        <div style={{ marginTop: 16 }}>
          <LeaveApprovalDefaultCard
            tenantId={tenantId}
            tenant={tenantQ.data}
            onSaved={() => void tenantQ.refetch()}
          />
        </div>
      )}
      {tab === 'roles' && (
        <><TabIntro>I <b>ruoli</b> raggruppano i permessi. Ogni utente riceve uno o più ruoli; i ruoli “di sistema” sono predefiniti (sola lettura), quelli “custom” li crei e gestisci tu.</TabIntro><RolesManager /></>
      )}
      {tab === 'role-perm' && (
        <><TabIntro>Scegli un <b>ruolo</b> dalla select in alto per vedere e gestire i suoi <b>permessi</b>: aggiungine di nuovi (liberi o dalla lista predefinita) o rimuovili.</TabIntro><RolePermissionsManager /></>
      )}
      {tab === 'crud-perm' && (
        <><TabIntro>Scegli un <b>ruolo</b> dalla select in alto e attiva/disattiva i permessi <b>Lettura · Creazione · Aggiornamento · Cancellazione</b> per ogni entità censita. I pulsanti in testata alle colonne agiscono <b>in blocco</b> sulle entità mostrate: se hai cercato qualcosa, valgono solo su quelle.</TabIntro><CrudPermissionsMatrix /></>
      )}
      {tab === 'routes' && (
        <><TabIntro>Scegli un <b>ruolo</b> dalla select in alto e abilita/disabilita l'<b>accesso alle rotte</b> (le pagine dell'app). Ogni rotta corrisponde a un permesso <code>r-p-…</code>.</TabIntro><RoutePermissionsManager /></>
      )}
      {tab === 'user-roles' && (
        <><TabIntro>Assegna o rimuovi i <b>ruoli</b> delle persone di quest’azienda. Puoi dare o togliere un ruolo solo se hai già tutti i suoi permessi; quelli che dà il sistema (User, Dipendente, Responsabile diretto) non si toccano. La stessa cosa, con dipendenti ed esterni insieme, è in <b>Organizzazione DP › Utenti e ruoli</b>.</TabIntro><UserRolesManager /></>
      )}
      {tab === 'user-tenants' && (
        <>
          <TabIntro>
            Gli <b>utenti associati</b> a questa azienda, con l'elenco filtrato dal sistema sull’azienda
            corrente. Per vedere e gestire in un colpo gli utenti di <b>tutte</b> le aziende che
            amministri c'è la vista dedicata: <Anchor component={Link} to="/admin/tenant-users" fw={600}>Utenti per azienda</Anchor>.
          </TabIntro>
          <UserTenantsManager tenantId={tenantId} tenantNome={tenantName} />
        </>
      )}
    </div>
  );

  const tabPills = (isScroll: boolean) => (
    <div style={{ display: 'flex', gap: 6, padding: isScroll ? '0 20px 2px' : 4, borderRadius: isScroll ? 0 : 12, background: isScroll ? 'transparent' : mw.surface, border: isScroll ? 0 : `1px solid ${mw.line}`, flexWrap: isScroll ? 'nowrap' : 'wrap', overflowX: isScroll ? 'auto' : undefined, width: isScroll ? undefined : 'fit-content', maxWidth: '100%', margin: isScroll ? '0 -20px' : undefined }}>
      {TABS.map((t) => {
        const active = tab === t.value;
        const Icon = t.icon;
        return (
          <button key={t.value} onClick={() => setTab(t.value)} className="mw-pressable"
            style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 7, height: rem(36), padding: '0 14px', borderRadius: isScroll ? 999 : 9, cursor: 'pointer', border: isScroll && !active ? `1px solid ${mw.line}` : 0, fontFamily: 'inherit', fontSize: 'var(--fs-compact)', fontWeight: 600, whiteSpace: 'nowrap', background: active ? mw.brandFill : isScroll ? '#fff' : 'transparent', color: active ? 'var(--on-brand)' : mw.ink500 }}>
            <Icon size={15} /> {t.label}
          </button>
        );
      })}
    </div>
  );

  if (isMobile) {
    return (
      <Stack gap={16} px={20} pt={16} pb={24}>
        <MobileHeader title="Gestione azienda" />
        {kpi}
        {tabPills(true)}
        {tabContent}
      </Stack>
    );
  }

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconBuilding size={22} />}
        title="Gestione azienda"
        sub={`Ruoli, permessi, rotte e utenti dell’azienda ${tenantName}.`}
        actions={tenantId ? (
          <Button component={Link} to={`/admin/tenants/${tenantId}`} variant="light" rightSection={<IconArrowRight size={16} />}>
            Scheda completa
          </Button>
        ) : undefined}
      />
      <Stack gap="lg">
        {kpi}
        {tabPills(false)}
        {tabContent}
      </Stack>
    </MaeShell>
  );
}

/** Etichetta/sezione/icona pagina per slug (ultimo segmento del path), da STATIC_ROUTES. */
const ROUTE_META_BY_SLUG = (() => {
  const m = new Map<string, { label: string; section: string; icon: (typeof STATIC_ROUTES)[number]['icon'] }>();
  for (const r of STATIC_ROUTES) {
    const seg = r.to.split('/').filter(Boolean).pop() ?? '';
    if (seg && !m.has(seg)) m.set(seg, { label: r.label, section: r.section, icon: r.icon });
  }
  return m;
})();

/** Gestione permessi di rotta: scegli un ruolo, abilita/disabilita l'accesso alle rotte (claim r-p-*). */
function RoutePermissionsManager() {
  const qc = useQueryClient();
  const { decoded } = useAuth();
  const isSuper = decoded?.superadmin === 'True';
  const isAdmin = isSuper || (Array.isArray(decoded?.claims) && (decoded!.claims as string[]).includes('isAdmin'));
  const myUserId = (decoded?.userId as string | undefined) ?? decoded?.sub ?? '';
  const [roleId, setRoleId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const rolesQ = useQuery({ queryKey: ['roles'], queryFn: () => fetchList<Role>('roles', { sort: ['name'], page: { size: 100 } }) });
  const roles = useMemo(() => (rolesQ.data?.data ?? []).filter(isRoleAssignableToInternal), [rolesQ.data]);
  const role = roles.find((r) => r.id === roleId);

  const myRolesQ = useQuery({
    queryKey: ['my-role-level', myUserId],
    enabled: Boolean(myUserId) && !isAdmin,
    queryFn: () => fetchList<UserRole>('user-roles', { filter: { 'user.id': { eq: myUserId } }, include: ['role'], page: { size: 100 } }),
  });
  const myLevel = useMemo(() => {
    if (isAdmin) return Number.POSITIVE_INFINITY;
    const levels = (myRolesQ.data?.data ?? []).map((ur) => ur.role?.level ?? 0);
    return levels.length ? Math.max(...levels) : null;
  }, [myRolesQ.data, isAdmin]);
  const editable = Boolean(role) && (isAdmin || (role?.isFromMaster !== true && myLevel != null && myLevel > (role?.level ?? 0)));

  // Catalogo rotte = unione di: (1) i permessi r-p-* autoritativi del seed BE (label IT),
  // (2) TUTTE le pagine reali dell'app (STATIC_ROUTES) → r-p-<slug-del-path>. Deduplicato per perm.
  const routes = useMemo(() => {
    const map = new Map<string, { perm: string; label: string; section?: string; icon?: (typeof STATIC_ROUTES)[number]['icon'] }>();
    for (const rp of ROUTE_PERMISSIONS) {
      const meta = ROUTE_META_BY_SLUG.get(rp.perm.slice(4));
      map.set(rp.perm, { perm: rp.perm, label: rp.label, section: meta?.section, icon: meta?.icon });
    }
    for (const r of STATIC_ROUTES) {
      const slug = r.to === '/' ? 'home' : r.to.replace(/^\//, '').replace(/\//g, '-');
      const perm = `r-p-${slug}`;
      if (!map.has(perm)) map.set(perm, { perm, label: r.label, section: r.section, icon: r.icon });
    }
    const q = search.trim().toLowerCase();
    return [...map.values()]
      .filter((r) => !q || r.label.toLowerCase().includes(q) || r.perm.toLowerCase().includes(q))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [search]);

  const claimsQ = useQuery({ queryKey: ['role-claims', roleId], enabled: Boolean(roleId), queryFn: () => fetchRoleClaims(roleId!) });
  const claimByValue = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of claimsQ.data?.data ?? []) if (c.claimValue) m.set(c.claimValue, c.id);
    return m;
  }, [claimsQ.data]);

  const toggle = useMutation({
    mutationFn: async ({ perm, on, claimId }: { perm: string; on: boolean; claimId?: string }) => {
      if (on) { await addRoleClaim(roleId!, 'route', perm); return; }
      /*
       * ⚠️ Spegnere ha bisogno dell'id della riga da cancellare, e quell'id si legge dall'elenco
       * dei permessi del ruolo. Finché quell'elenco si fermava a cento righe su ~474, qui
       * `claimId` arrivava spesso `undefined` — e un `else if (claimId)` senza ramo alternativo
       * **riportava successo senza fare niente**: nessun errore, nessuna modifica, l'interruttore
       * che torna com'era. È il difetto segnalato come «clicco lo switch e non succede nulla».
       *
       * La causa è chiusa in `fetchRoleClaims`, che ora legge tutte le pagine. Questa resta a
       * presidio: se l'id manca di nuovo — per una lettura ancora in corso, o per un permesso
       * creato altrove — si dice, invece di tacere.
       */
      if (!claimId) throw new Error('Permesso non trovato fra quelli del ruolo: ricarica la pagina e riprova.');
      await removeRoleClaim(claimId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['role-claims', roleId] }),
    onError: (e) => notifyError(e, 'Errore salvataggio rotta'),
  });

  // Abilita/disabilita in blocco tutte le rotte attualmente visibili (rispetta la ricerca).
  const bulkToggle = useMutation({
    mutationFn: async (on: boolean) => {
      for (const r of routes) {
        const claimId = claimByValue.get(r.perm);
        if (on && !claimId) await addRoleClaim(roleId!, 'route', r.perm);
        else if (!on && claimId) await removeRoleClaim(claimId);
      }
    },
    onSuccess: (_d, on) => {
      qc.invalidateQueries({ queryKey: ['role-claims', roleId] });
      notifications.show({ color: 'teal', message: on ? 'Tutte le rotte abilitate.' : 'Tutte le rotte disabilitate.' });
    },
    onError: (e) => notifyError(e, 'Errore aggiornamento massivo'),
  });

  const enabledCount = useMemo(() => routes.filter((r) => claimByValue.has(r.perm)).length, [routes, claimByValue]);
  const busy = toggle.isPending || bulkToggle.isPending;

  return (
    <Stack gap="md">
      <Card withBorder radius="lg" padding="md">
        <Group justify="space-between" wrap="wrap" gap="sm" align="flex-end">
          <Select
            label="Ruolo"
            placeholder="Scegli un ruolo…"
            data={roles.map((r) => ({ value: r.id, label: r.name ?? r.id }))}
            value={roleId}
            onChange={setRoleId}
            searchable
            leftSection={<IconShieldCheck size={16} />}
            w={340}
          />
          {roleId && (
            <TextInput label="Cerca rotta" placeholder="…" leftSection={<IconSearch size={16} />} value={search} onChange={(e) => setSearch(e.currentTarget.value)} w={260} />
          )}
        </Group>
      </Card>

      {!roleId && (
        <EmptyState icon={IconRoute} title="Scegli un ruolo" description="Seleziona un ruolo nella select in alto per gestirne l'accesso alle rotte." py="xl" />
      )}

      {roleId && !editable && (
        <Alert color={role?.isFromMaster ? 'gray' : 'yellow'} variant="light" icon={<IconLock size={18} />}>
          {role?.isFromMaster
            ? 'Ruolo di sistema: gestito centralmente. Solo un amministratore può modificarlo.'
            : 'Sola lettura: puoi modificare solo i ruoli di livello inferiore al tuo.'}
        </Alert>
      )}

      {roleId && (
        <>
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Text size="sm" c="dimmed">
              {enabledCount} / {routes.length} rotte abilitate{search.trim() ? ' (filtrate)' : ''}
            </Text>
            {editable && (
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="light"
                  color="teal"
                  leftSection={<IconChecks size={16} />}
                  loading={bulkToggle.isPending}
                  disabled={busy || routes.length === 0 || enabledCount === routes.length}
                  onClick={() => bulkToggle.mutate(true)}
                >
                  Abilita tutte
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  color="gray"
                  leftSection={<IconSquareOff size={16} />}
                  loading={bulkToggle.isPending}
                  disabled={busy || enabledCount === 0}
                  onClick={() => bulkToggle.mutate(false)}
                >
                  Disabilita tutte
                </Button>
              </Group>
            )}
          </Group>
          <Card withBorder radius="lg" padding={0}>
            {claimsQ.isLoading ? (
              <Group justify="center" py="xl"><Loader /></Group>
            ) : (
              <ScrollArea type="auto">
                <Table verticalSpacing="xs" horizontalSpacing="md" highlightOnHover striped="even" miw={620}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ minWidth: 260 }}>Rotta</Table.Th>
                      <Table.Th>Nome del permesso</Table.Th>
                      <Table.Th ta="center" style={{ minWidth: 150 }}>Permesso di accesso</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {routes.map((r) => {
                      const has = claimByValue.has(r.perm);
                      const Icon = r.icon;
                      return (
                        <Table.Tr key={r.perm}>
                          <Table.Td>
                            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                              <ThemeIcon variant="light" size={32} radius="md">{Icon ? <Icon size={18} /> : <IconRoute size={18} />}</ThemeIcon>
                              <div style={{ minWidth: 0 }}>
                                <Text size="sm" fw={500} lineClamp={1}>{r.label}</Text>
                                {r.section && <Text size="xs" c="dimmed">{r.section}</Text>}
                              </div>
                            </Group>
                          </Table.Td>
                          <Table.Td><Text size="xs" c="dimmed" style={{ ...MW_CODE_STYLE }}>{r.perm}</Text></Table.Td>
                          <Table.Td ta="center">
                            <Switch
                              checked={has}
                              disabled={!editable || toggle.isPending}
                              onChange={() => toggle.mutate({ perm: r.perm, on: !has, claimId: claimByValue.get(r.perm) })}
                              aria-label={`${r.perm} ${has ? 'attivo' : 'disattivo'}`}
                              styles={{ root: { display: 'inline-flex' } }}
                            />
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                    {routes.length === 0 && (
                      <Table.Tr><Table.Td colSpan={3}><Text c="dimmed" ta="center" py="md">Nessuna rotta trovata.</Text></Table.Td></Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}
          </Card>
        </>
      )}
    </Stack>
  );
}

/** Etichetta utente: "Nome Cognome" con fallback su email/id. */
function userFullName(u: { firstName?: string; lastName?: string; email?: string; id?: string } | null | undefined): string {
  if (!u) return '—';
  const n = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return n || u.email || u.id || '—';
}

/**
 * Ruoli degli utenti (user-roles): assegna/rimuovi ruoli con due viste (per utenti / per ruoli).
 *
 * ⚠️ CHI PUÒ COSA: la regola è **quella del sistema** (22-10-2026). Si può dare o togliere un
 * profilo solo se si hanno già tutti i suoi permessi (`CanAssignRoleToUser`), e la lista la dà
 * `get-assignable-claims` (`fetchAssignableRoleIds`), come nella scheda utente. Prima qui c'era
 * una regola propria a **livelli**, che il sistema non conosce: scartava ogni profilo del Master —
 * cioè, sul dev, **tutti** — e a chi non era superadmin non lasciava fare niente.
 *
 * Fuori anche i profili che il sistema dà e toglie da solo (`isAutoManagedProfile`).
 */
function UserRolesManager() {
  const qc = useQueryClient();
  const { decoded } = useAuth();
  const isSuper = decoded?.superadmin === 'True';
  const PAGE = 20;

  const [view, setView] = useState<'users' | 'roles'>('users');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [roleId, setRoleId] = useState<string | null>(null);

  /* Le persone di **quest'azienda**, dalle associazioni utente↔azienda (23-10-2026). Prima erano
     `GET /api/users` senza filtro, cioè tutta l'installazione (591 sul dev): da qui si dava un
     profilo anche a chi lavora in un'altra azienda, e il sistema lo accetta (task 2030 §6). */
  const companyId = useCurrentTenantId();
  const usersQ = useQuery({
    queryKey: ['tm-users', companyId],
    enabled: Boolean(companyId),
    queryFn: async () => {
      const rows = await fetchAllPages<{ id: string; type: 'user-tenants'; user?: UserRow | null }>('user-tenants', {
        filter: { 'tenant.id': { eq: companyId } },
        include: ['user'],
        fields: { 'user-tenants': ['user'], users: ['email', 'firstName', 'lastName', 'userName'] },
      });
      const byId = new Map<string, UserRow>();
      for (const r of rows) if (r.user?.id && !byId.has(r.user.id)) byId.set(r.user.id, r.user);
      return [...byId.values()].sort((a, b) => userFullName(a).localeCompare(userFullName(b), 'it'));
    },
  });
  const urQ = useQuery({
    queryKey: ['tm-user-roles'],
    // Il fieldset elenca anche `user` e `role`: senza i nomi delle relazioni il linkage
    // sparisce (misurato sul dev) e la tabella perde utente e ruolo in silenzio.
    queryFn: () => fetchAllPages<UserRole>('user-roles', {
      include: ['user', 'role'],
      fields: {
        'user-roles': ['user', 'role'],
        users: ['email', 'firstName', 'lastName'],
        roles: ['name', 'level', 'isFromMaster'],
      },
    }),
  });
  const rolesQ = useQuery({ queryKey: ['roles'], queryFn: () => fetchList<Role>('roles', { sort: ['name'], page: { size: 100 } }) });

  const assignableQ = useQuery({ queryKey: ['assignable-role-ids'], staleTime: 5 * 60_000, queryFn: fetchAssignableRoleIds });
  /** Il motivo per cui un profilo non ha i comandi, o `null` se si può gestire. */
  const lockReason = (role?: { id?: string; name?: string } | null): string | null => {
    if (!role?.id) return 'Ruolo non riconosciuto.';
    if (isAutoManagedProfile(role)) return AUTO_MANAGED_REASON;
    return isSuper || canAssign(assignableQ.data ?? null, role.id) ? null : UNASSIGNABLE_REASON;
  };
  const canManage = (role?: { id?: string; name?: string } | null) => lockReason(role) === null;

  const users = useMemo(() => usersQ.data ?? [], [usersQ.data]);
  const urs = useMemo(() => urQ.data ?? [], [urQ.data]);
  /* Fuori il portale e i profili non attivati (senza nome a schermo): come nella scheda utente. */
  const roles = useMemo(
    () => (rolesQ.data?.data ?? []).filter((r) => isRoleAssignableToInternal(r) && isRoleSelectable(r as RoleRow)),
    [rolesQ.data],
  );
  const roleById = useMemo(() => new Map((rolesQ.data?.data ?? []).map((r) => [r.id, r])), [rolesQ.data]);
  const labelOf = (r?: { id?: string; name?: string } | null) => {
    const full = r?.id ? roleById.get(r.id) : undefined;
    return full ? roleLabel(full as RoleRow) : (r?.name ?? r?.id ?? '—');
  };

  const byUser = useMemo(() => {
    const m = new Map<string, Array<{ urId: string; role: UserRole['role'] }>>();
    for (const ur of urs) { const uid = ur.user?.id; if (!uid) continue; (m.get(uid) ?? m.set(uid, []).get(uid)!).push({ urId: ur.id, role: ur.role }); }
    return m;
  }, [urs]);
  const byRole = useMemo(() => {
    const m = new Map<string, Array<{ urId: string; user: UserRole['user'] }>>();
    for (const ur of urs) { const rid = ur.role?.id; if (!rid) continue; (m.get(rid) ?? m.set(rid, []).get(rid)!).push({ urId: ur.id, user: ur.user }); }
    return m;
  }, [urs]);

  const addRole = useMutation({
    mutationFn: ({ userId, roleId: rid }: { userId: string; roleId: string }) =>
      createOne('user-roles', { relationships: { user: { data: { type: 'users', id: userId } }, role: { data: { type: 'roles', id: rid } } } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tm-user-roles'] }),
    onError: (e) => notifyError(e, 'Errore assegnazione ruolo'),
  });
  const removeRole = useMutation({
    mutationFn: (urId: string) => deleteOne('user-roles', urId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tm-user-roles'] }),
    onError: (e) => notifyError(e, 'Errore rimozione ruolo'),
  });
  const busy = addRole.isPending || removeRole.isPending;

  const loading = usersQ.isLoading || urQ.isLoading || rolesQ.isLoading;
  /* Anche i profili: se non arrivano, «Nessun ruolo assegnabile» sarebbe falso. */
  const loadError = usersQ.error ?? urQ.error ?? rolesQ.error;

  /** Togliere chiede conferma: un profilo porta permessi, e il clic sulla X è facile da sbagliare. */
  const confirmRemove = (urId: string, who: string, what: string) => modals.openConfirmModal({
    title: 'Togliere il ruolo?',
    children: <Text size="sm"><strong>{who}</strong> perde il ruolo <strong>{what}</strong> e i permessi che porta, a meno che non li abbia da un altro ruolo.</Text>,
    labels: { confirm: 'Togli', cancel: 'Annulla' },
    confirmProps: { color: 'red' },
    onConfirm: () => removeRole.mutate(urId),
  });

  // ---- Vista PER UTENTI ----
  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => [u.firstName, u.lastName, u.email, u.userName].some((v) => v?.toLowerCase().includes(q)));
  }, [users, search]);
  const pageCount = Math.max(1, Math.ceil(filteredUsers.length / PAGE));
  const curPage = Math.min(page, pageCount);
  const pageUsers = filteredUsers.slice((curPage - 1) * PAGE, curPage * PAGE);

  // ---- Vista PER RUOLI ----
  const selectedRole = roles.find((r) => r.id === roleId);
  const selectedLabel = selectedRole ? labelOf(selectedRole) : '';
  const roleUsers = roleId ? (byRole.get(roleId) ?? []) : [];
  const assignedUserIds = new Set(roleUsers.map((x) => x.user?.id));
  const assignableUsers = users.filter((u) => !assignedUserIds.has(u.id));

  function RolePill({ urId, role, who }: { urId: string; role: UserRole['role']; who: string }) {
    const removable = canManage(role);
    const auto = isAutoManagedProfile(role);
    return (
      <Tooltip label={lockReason(role) ?? ''} disabled={removable} withArrow multiline w={280}>
      <Badge
        key={urId}
        variant="light"
        color={role?.isFromMaster ? 'gray' : 'blue'}
        size="lg"
        radius="sm"
        pr={removable ? 4 : undefined}
        rightSection={removable ? (
          <Tooltip label="Rimuovi ruolo" withArrow>
            <ActionIcon loading={removeRole.isPending} size="xs" color="red" variant="transparent" disabled={busy} onClick={() => confirmRemove(urId, who, labelOf(role))} aria-label="Rimuovi ruolo">
              <IconTrash size={13} />
            </ActionIcon>
          </Tooltip>
        ) : undefined}
      >
        {labelOf(role)}{auto ? ' · automatico' : ''}
      </Badge>
      </Tooltip>
    );
  }

  function AddRoleMenu({ userId, assignedRoleIds }: { userId: string; assignedRoleIds: Set<string | undefined> }) {
    const assignable = roles.filter((r) => canManage(r) && !assignedRoleIds.has(r.id));
    return (
      <Menu shadow="md" width={240} position="bottom-end" withinPortal>
        <Menu.Target>
          <Tooltip label="Aggiungi ruolo" withArrow>
            <ActionIcon variant="light" color="blue" radius="md" size="md" disabled={busy || assignable.length === 0} aria-label="Aggiungi ruolo">
              <IconPlus size={16} />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Aggiungi ruolo</Menu.Label>
          {assignable.length === 0 ? (
            <Menu.Item disabled>Nessun ruolo assegnabile</Menu.Item>
          ) : (
            assignable.map((r) => (
              <Menu.Item key={r.id} disabled={addRole.isPending} leftSection={<IconShieldCheck size={14} />} onClick={() => addRole.mutate({ userId, roleId: r.id })}>
                {labelOf(r)}
              </Menu.Item>
            ))
          )}
        </Menu.Dropdown>
      </Menu>
    );
  }

  return (
    <Stack gap="md">
      <Card withBorder radius="lg" padding="md">
        <Group justify="space-between" wrap="wrap" gap="sm" align="flex-end">
          <SegmentedControl
            value={view}
            onChange={(v) => { setView(v as 'users' | 'roles'); setPage(1); }}
            data={[{ label: 'Per utenti', value: 'users' }, { label: 'Per ruoli', value: 'roles' }]}
          />
          {view === 'users' ? (
            <TextInput
              label="Cerca utente"
              placeholder="Nome, cognome o email…"
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => { setSearch(e.currentTarget.value); setPage(1); }}
              w={300}
            />
          ) : (
            <Select
              label="Ruolo"
              placeholder="Scegli un ruolo…"
              data={roles.map((r) => ({ value: r.id, label: labelOf(r) }))}
              value={roleId}
              onChange={setRoleId}
              searchable
              leftSection={<IconShieldCheck size={16} />}
              w={340}
            />
          )}
        </Group>
      </Card>

      {loadError && <QueryErrorAlert error={loadError} />}
      {loading ? (
        <Group justify="center" py="xl"><Loader /></Group>
      ) : view === 'users' ? (
        <>
          <Card withBorder radius="lg" padding={0}>
            <ScrollArea type="auto">
              <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover striped="even" miw={720}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ minWidth: 160 }}>Nome</Table.Th>
                    <Table.Th style={{ minWidth: 160 }}>Cognome</Table.Th>
                    <Table.Th style={{ minWidth: 220 }}>Email</Table.Th>
                    <Table.Th style={{ minWidth: 280 }}>Ruoli</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pageUsers.map((u) => {
                    const assigned = byUser.get(u.id) ?? [];
                    const assignedRoleIds = new Set(assigned.map((a) => a.role?.id));
                    return (
                      <Table.Tr key={u.id}>
                        <Table.Td><Text size="sm">{u.firstName || '—'}</Text></Table.Td>
                        <Table.Td><Text size="sm">{u.lastName || '—'}</Text></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{u.email ?? '—'}</Text></Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            {assigned.length === 0 && <Text size="sm" c="dimmed">Nessun ruolo</Text>}
                            {assigned.map((a) => <RolePill key={a.urId} urId={a.urId} role={a.role} who={userFullName(u)} />)}
                            <AddRoleMenu userId={u.id} assignedRoleIds={assignedRoleIds} />
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                  {pageUsers.length === 0 && (
                    <Table.Tr><Table.Td colSpan={4}><Text c="dimmed" ta="center" py="md">Nessun utente trovato.</Text></Table.Td></Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Card>
          <Group justify="space-between" wrap="wrap">
            <Text size="sm" c="dimmed">{filteredUsers.length} utenti{search.trim() ? ' (filtrati)' : ''}</Text>
            {pageCount > 1 && <Pagination total={pageCount} value={curPage} onChange={setPage} size="sm" />}
          </Group>
        </>
      ) : !roleId ? (
        <EmptyState icon={IconShieldCheck} title="Scegli un ruolo" description="Seleziona un ruolo nella select in alto per vederne gli utenti e assegnarlo." py="xl" />
      ) : (
        <>
          {!canManage(selectedRole) && (
            <Alert color="gray" variant="light" icon={<IconLock size={18} />}>
              {lockReason(selectedRole)}
            </Alert>
          )}
          {canManage(selectedRole) && (
            <Select
              placeholder="Assegna questo ruolo a un utente…"
              data={assignableUsers.map((u) => ({ value: u.id, label: `${userFullName(u)}${u.email ? ` · ${u.email}` : ''}` }))}
              value={null}
              onChange={(v) => { if (v) addRole.mutate({ userId: v, roleId }); }}
              searchable
              nothingFoundMessage="Nessun utente"
              leftSection={<IconPlus size={16} />}
              disabled={busy}
              w={420}
            />
          )}
          <Card withBorder radius="lg" padding="md">
            <Text size="sm" fw={600} mb="xs">Utenti con il ruolo «{selectedLabel || roleId}»</Text>
            {roleUsers.length === 0 ? (
              <Text size="sm" c="dimmed">Nessun utente ha questo ruolo.</Text>
            ) : (
              <Stack gap="xs">
                {roleUsers.map((x) => (
                  <Group key={x.urId} justify="space-between" wrap="nowrap" style={{ borderBottom: '1px solid var(--mantine-color-default-border)', paddingBottom: 6 }}>
                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                      <ThemeIcon variant="light" size={32} radius="xl"><IconUserShield size={16} /></ThemeIcon>
                      <div style={{ minWidth: 0 }}>
                        <Text size="sm" fw={500} lineClamp={1}>{userFullName(x.user)}</Text>
                        {x.user?.email && <Text size="xs" c="dimmed" lineClamp={1}>{x.user.email}</Text>}
                      </div>
                    </Group>
                    {canManage(selectedRole) && (
                      <Tooltip label="Rimuovi ruolo all'utente" withArrow>
                        <ActionIcon loading={removeRole.isPending} color="red" variant="light" disabled={busy} onClick={() => confirmRemove(x.urId, userFullName(x.user), selectedLabel)} aria-label="Rimuovi ruolo">
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                ))}
              </Stack>
            )}
          </Card>
        </>
      )}
    </Stack>
  );
}

/** Gestione ruoli del tenant: lista + crea/modifica/elimina (i ruoli di sistema sono read-only). */
function RolesManager() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLevel, setNewLevel] = useState<number | ''>(0);
  const [editRole, setEditRole] = useState<Role | null>(null);

  const rolesQ = useQuery({
    queryKey: ['roles'],
    queryFn: () => fetchList<Role>('roles', { sort: ['name'], page: { size: 100 } }),
  });
  const filtered = useMemo(() => {
    const list = rolesQ.data?.data ?? [];
    const q = search.trim().toLowerCase();
    return list.filter((r) => !q || (r.name ?? '').toLowerCase().includes(q));
  }, [rolesQ.data, search]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['roles'] });

  const createMut = useMutation({
    mutationFn: () => createOne('roles', { attributes: { name: newName.trim(), level: typeof newLevel === 'number' ? newLevel : 0 } }),
    onSuccess: () => { invalidate(); notifications.show({ color: 'green', message: 'Ruolo creato' }); setNewName(''); setNewLevel(0); setCreating(false); },
    onError: (e) => notifyError(e, 'Errore creazione'),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => deleteOne('roles', id),
    onSuccess: () => { invalidate(); notifications.show({ color: 'green', message: 'Ruolo eliminato' }); },
    onError: (e) => notifyError(e, 'Errore eliminazione'),
  });

  const confirmDelete = (r: Role) => modals.openConfirmModal({
    title: 'Elimina ruolo',
    children: <Text size="sm">Eliminare il ruolo <strong>{r.name}</strong>? L&apos;operazione non è reversibile.</Text>,
    labels: { confirm: 'Elimina', cancel: 'Annulla' },
    confirmProps: { color: 'red' },
    onConfirm: () => delMut.mutate(r.id),
  });

  if (rolesQ.isError) return <QueryErrorAlert error={rolesQ.error} onRetry={() => void rolesQ.refetch()} />;

  return (
    <Stack gap="md">
      <Card withBorder radius="lg" padding="sm">
        <Group justify="space-between" wrap="wrap" gap="sm">
          <TextInput placeholder="Cerca ruolo…" leftSection={<IconSearch size={16} />} value={search} onChange={(e) => setSearch(e.currentTarget.value)} w={320} />
          <Button leftSection={<IconPlus size={16} />} variant={creating ? 'light' : 'filled'} onClick={() => setCreating((v) => !v)}>
            {creating ? 'Chiudi' : 'Aggiungi ruolo'}
          </Button>
        </Group>
        <Collapse expanded={creating}>
          <Group align="flex-end" gap="sm" mt="md">
            <TextInput label="Nome del ruolo" placeholder="Es. Commerciale" value={newName} onChange={(e) => setNewName(e.currentTarget.value)} style={{ flex: 1, minWidth: 220 }} />
            <NumberInput label="Livello" min={0} max={999} value={newLevel} onChange={(v) => setNewLevel(typeof v === 'number' ? v : '')} w={130} />
            <Button leftSection={<IconDeviceFloppy size={16} />} loading={createMut.isPending} disabled={!newName.trim()} onClick={() => createMut.mutate()}>Salva</Button>
          </Group>
        </Collapse>
      </Card>

      <Card withBorder radius="lg" padding={0}>
        {rolesQ.isLoading ? (
          <Group justify="center" py="xl"><Loader /></Group>
        ) : filtered.length === 0 ? (
          <EmptyState icon={IconShieldCheck} title="Nessun ruolo"
            description="I ruoli raggruppano i permessi e si assegnano agli utenti: senza, nessuno ha accesso oltre al minimo."
            actionLabel="Aggiungi ruolo" onAction={() => setCreating(true)} py="xl" />
        ) : (
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nome del ruolo</Table.Th>
                <Table.Th w={120} ta="center">Livello</Table.Th>
                <Table.Th w={130}>Origine</Table.Th>
                <Table.Th w={110} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td><Text fw={500}>{r.name ?? '—'}</Text></Table.Td>
                  <Table.Td ta="center"><Badge variant="light" color="gray">{r.level ?? 0}</Badge></Table.Td>
                  <Table.Td>{r.isFromMaster ? <Badge color="gray" variant="light">Sistema</Badge> : <Badge color="blue" variant="light">Custom</Badge>}</Table.Td>
                  <Table.Td>
                    {!r.isFromMaster && (
                      <Group gap={4} wrap="nowrap" justify="flex-end">
                        <Tooltip label="Modifica"><ActionIcon variant="light" color="yellow" onClick={() => setEditRole(r)}><IconPencil size={16} /></ActionIcon></Tooltip>
                        <Tooltip label="Elimina"><ActionIcon variant="light" color="red" onClick={() => confirmDelete(r)}><IconTrash size={16} /></ActionIcon></Tooltip>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <RoleEditModal role={editRole} onClose={() => setEditRole(null)} onSaved={invalidate} />
    </Stack>
  );
}

function RoleEditModal({ role, onClose, onSaved }: { role: Role | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [level, setLevel] = useState<number | ''>(0);
  useEffect(() => { if (role) { setName(role.name ?? ''); setLevel(role.level ?? 0); } }, [role]);

  const save = useMutation({
    mutationFn: () => updateOne('roles', { id: role!.id, attributes: { name: name.trim(), level: typeof level === 'number' ? level : 0 } }),
    onSuccess: () => { onSaved(); notifications.show({ color: 'green', message: 'Ruolo aggiornato' }); onClose(); },
    onError: (e) => notifyError(e, 'Errore salvataggio'),
  });

  return (
    <Modal opened={Boolean(role)} onClose={onClose} title="Modifica ruolo" centered>
      <Stack>
        <TextInput label="Nome del ruolo" required value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <NumberInput label="Livello" min={0} max={999} value={level} onChange={(v) => setLevel(typeof v === 'number' ? v : '')} />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Annulla</Button>
          <Button leftSection={<IconDeviceFloppy size={16} />} loading={save.isPending} disabled={!name.trim()} onClick={() => save.mutate()}>Salva</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Associazioni Ruolo ÷ Permessi: scegli un ruolo, gestisci i suoi claim (aggiungi/rimuovi). */
function RolePermissionsManager() {
  const qc = useQueryClient();
  const [roleId, setRoleId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [name, setName] = useState('');

  const { decoded } = useAuth();
  const isSuper = decoded?.superadmin === 'True';
  const myClaims = Array.isArray(decoded?.claims) ? (decoded!.claims as string[]) : [];
  // Un amministratore (claim isAdmin) o il superadmin possono sempre gestire i permessi.
  const isAdmin = isSuper || myClaims.includes('isAdmin');
  const myUserId = (decoded?.userId as string | undefined) ?? decoded?.sub ?? '';

  const rolesQ = useQuery({ queryKey: ['roles'], queryFn: () => fetchList<Role>('roles', { sort: ['name'], page: { size: 100 } }) });
  const roles = useMemo(() => (rolesQ.data?.data ?? []).filter(isRoleAssignableToInternal), [rolesQ.data]);
  const role = roles.find((r) => r.id === roleId);
  const isMaster = role?.isFromMaster === true;

  // Livello del ruolo più alto dell'utente corrente: si possono modificare solo
  // i ruoli di livello INFERIORE al proprio (superadmin bypassa).
  const myRolesQ = useQuery({
    queryKey: ['my-role-level', myUserId],
    enabled: Boolean(myUserId) && !isAdmin,
    queryFn: () => fetchList<UserRole>('user-roles', { filter: { 'user.id': { eq: myUserId } }, include: ['role'], page: { size: 100 } }),
  });
  const myLevel = useMemo(() => {
    if (isAdmin) return Number.POSITIVE_INFINITY;
    const levels = (myRolesQ.data?.data ?? []).map((ur) => ur.role?.level ?? 0);
    return levels.length ? Math.max(...levels) : null;
  }, [myRolesQ.data, isAdmin]);

  // Posso modificare i permessi del ruolo selezionato?
  // Admin/superadmin: sempre (anche i ruoli di sistema — sarà il BE a rifiutare se non permesso).
  // Non-admin: solo ruoli NON di sistema e di livello inferiore al proprio.
  const levelOk = myLevel != null && role != null && myLevel > (role.level ?? 0);
  const editable = Boolean(role) && (isAdmin || (!isMaster && levelOk));

  const claimsQ = useQuery({ queryKey: ['role-claims', roleId], enabled: Boolean(roleId), queryFn: () => fetchRoleClaims(roleId!) });
  const claims = claimsQ.data?.data ?? [];

  /*
   * Lista predefinita = valori claim **distinti** su tutti i ruoli.
   *
   * ⚠️ Qui serve davvero l'elenco intero, e per questo va detto cosa costava prima: senza tetto
   * esplicito `fetchAllPages` si ferma al suo default di **2.000 righe**, e i `role-claims` sul dev
   * sono **2.867**. Ottocentosessantasette permessi non comparivano nella tendina — senza errore e
   * senza avviso — quindi chi ne assegnava uno che stava oltre non lo trovava e concludeva che non
   * esistesse. Su una schermata di permessi è la peggiore delle risposte sbagliate: sembra un
   * divieto ed è una lista tagliata.
   *
   * Il fieldset è ciò che rende sostenibile il tetto alto: qui si legge **un attributo** per riga,
   * quindi tremila righe sono qualche decina di KB in tre richieste. Una pagina grande senza
   * fieldset sarebbe solo il payload sbagliato tutto in una volta.
   */
  const allClaimsQ = useQuery({
    queryKey: ['all-role-claims-catalog'],
    queryFn: () => fetchAllPages<RoleClaim>(
      'role-claims',
      { fields: { 'role-claims': ['claimValue'] } },
      20_000,
    ),
  });
  /**
   * La tendina offre solo ciò che al ruolo **manca**.
   *
   * I permessi che ha già stanno nella scheda qui sopra, con il loro cestino: riproporli qui
   * dava una tendina in cui non si distingueva il nuovo dal già fatto, e sceglierne uno
   * significava chiedere al server un duplicato — cioè un errore, per un'azione che sembrava
   * legittima.
   */
  const predefined = useMemo(() => {
    const alreadyOnThisRole = new Set((claimsQ.data?.data ?? []).map((c) => c.claimValue).filter(Boolean) as string[]);
    const s = new Set<string>();
    for (const c of allClaimsQ.data ?? []) if (c.claimValue && !alreadyOnThisRole.has(c.claimValue)) s.add(c.claimValue);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [allClaimsQ.data, claimsQ.data]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['role-claims', roleId] });
    qc.invalidateQueries({ queryKey: ['all-role-claims-catalog'] });
  };
  const add = useMutation({
    mutationFn: () => addRoleClaim(roleId!, 'custom', name.trim()),
    onSuccess: () => { setName(''); invalidate(); notifications.show({ color: 'green', message: 'Permesso aggiunto' }); },
    onError: (e) => notifyError(e, 'Errore aggiunta'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => removeRoleClaim(id),
    onSuccess: () => { invalidate(); notifications.show({ color: 'green', message: 'Permesso rimosso' }); },
    onError: (e) => notifyError(e, 'Errore rimozione'),
  });

  const filtered = claims.filter((c) => !search || (c.claimValue ?? '').toLowerCase().includes(search.toLowerCase()));

  return (
    <Stack gap="md">
      {/* Selettore ruolo */}
      <Card withBorder radius="lg" padding="md">
        <Group justify="space-between" wrap="wrap" gap="sm" align="flex-end">
          <Select
            label="Ruolo"
            placeholder="Scegli un ruolo…"
            data={roles.map((r) => ({ value: r.id, label: r.name ?? r.id }))}
            value={roleId}
            onChange={setRoleId}
            searchable
            leftSection={<IconShieldCheck size={16} />}
            w={340}
          />
          {roleId && (
            <TextInput label="Cerca permesso" placeholder="…" leftSection={<IconSearch size={16} />} value={search} onChange={(e) => setSearch(e.currentTarget.value)} w={260} />
          )}
        </Group>
      </Card>

      {!roleId && (
        <EmptyState icon={IconKey} title="Scegli un ruolo" description="Seleziona un ruolo nella select in alto per vederne e gestirne i permessi." py="xl" />
      )}

      {roleId && (
        <>
          {!editable && isMaster && (
            <Alert color="gray" variant="light" icon={<IconLock size={18} />}>
              Ruolo di sistema: i permessi sono gestiti centralmente. Solo un amministratore può modificarli.
            </Alert>
          )}
          {!editable && !isMaster && (
            <Alert color="yellow" variant="light" icon={<IconLock size={18} />}>
              Sola lettura: puoi modificare i permessi solo dei ruoli di <b>livello inferiore</b> al tuo
              {myLevel != null && Number.isFinite(myLevel) ? ` (il tuo livello è ${myLevel})` : ''}.
            </Alert>
          )}

          <Card withBorder radius="lg" padding="md">
            <Group justify="space-between" mb="sm">
              <Text fw={600}>Permessi del ruolo</Text>
              <Badge variant="light">{filtered.length}</Badge>
            </Group>
            {claimsQ.isLoading ? (
              <Group justify="center" py="xl"><Loader /></Group>
            ) : filtered.length === 0 ? (
              <Text c="dimmed" size="sm">{search ? 'Nessun permesso per la ricerca.' : 'Questo ruolo non ha permessi.'}</Text>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
                {filtered.map((c) => (
                  <Group
                    key={c.id}
                    justify="space-between"
                    wrap="nowrap"
                    p="xs"
                    style={{ borderRadius: 8, border: '1px solid var(--mantine-color-default-border)', background: 'var(--mantine-color-default)' }}
                  >
                    <Text size="sm" lineClamp={1} title={c.claimValue ?? ''}>{c.claimValue ?? '—'}</Text>
                    {editable && (
                      <Tooltip label="Rimuovi">
                        <ActionIcon loading={remove.isPending} color="red" variant="light" onClick={() => remove.mutate(c.id)} aria-label="Rimuovi permesso">
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                ))}
              </SimpleGrid>
            )}
          </Card>

          {editable && (
            <Card withBorder radius="lg" padding="md">
              <Text fw={600} mb="sm">Aggiungi un permesso</Text>
              <Group align="flex-end" gap="sm" wrap="wrap">
                <TextInput label="Nome del permesso" placeholder="es. canSeeAllTenants" value={name} onChange={(e) => setName(e.currentTarget.value)} style={{ flex: 1, minWidth: 220 }} />
                <Select
                  label="Oppure dalla lista predefinita"
                  /* Elenco vuoto = il ruolo li ha già tutti: dirlo, invece di mostrare una
                     tendina vuota che si legge come «la lista non ha caricato». */
                  placeholder={allClaimsQ.isFetching ? 'Carico…' : predefined.length ? '---' : 'Nessun altro permesso da aggiungere'}
                  disabled={!allClaimsQ.isFetching && predefined.length === 0}
                  data={predefined}
                  value={predefined.includes(name) ? name : null}
                  onChange={(v) => v && setName(v)}
                  searchable
                  clearable
                  nothingFoundMessage="Nessun permesso disponibile con questo nome."
                  w={300}
                  rightSection={allClaimsQ.isFetching ? <Loader size="xs" /> : null}
                />
                <Button leftSection={<IconDeviceFloppy size={16} />} loading={add.isPending} disabled={!name.trim()} onClick={() => add.mutate()}>Salva</Button>
              </Group>
            </Card>
          )}
        </>
      )}
    </Stack>
  );
}

/** Matrice Permessi CRUD: scegli un ruolo e attiva/disattiva read/create/update/delete per entità. */
function CrudPermissionsMatrix() {
  const qc = useQueryClient();
  const { decoded } = useAuth();
  const isSuper = decoded?.superadmin === 'True';
  const isAdmin = isSuper || (Array.isArray(decoded?.claims) && (decoded!.claims as string[]).includes('isAdmin'));
  const myUserId = (decoded?.userId as string | undefined) ?? decoded?.sub ?? '';

  const [roleId, setRoleId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const rolesQ = useQuery({ queryKey: ['roles'], queryFn: () => fetchList<Role>('roles', { sort: ['name'], page: { size: 100 } }) });
  const roles = useMemo(() => (rolesQ.data?.data ?? []).filter(isRoleAssignableToInternal), [rolesQ.data]);
  const role = roles.find((r) => r.id === roleId);

  const myRolesQ = useQuery({
    queryKey: ['my-role-level', myUserId],
    enabled: Boolean(myUserId) && !isAdmin,
    queryFn: () => fetchList<UserRole>('user-roles', { filter: { 'user.id': { eq: myUserId } }, include: ['role'], page: { size: 100 } }),
  });
  const myLevel = useMemo(() => {
    if (isAdmin) return Number.POSITIVE_INFINITY;
    const levels = (myRolesQ.data?.data ?? []).map((ur) => ur.role?.level ?? 0);
    return levels.length ? Math.max(...levels) : null;
  }, [myRolesQ.data, isAdmin]);
  const editable = Boolean(role) && (isAdmin || (role?.isFromMaster !== true && myLevel != null && myLevel > (role?.level ?? 0)));

  const claimsQ = useQuery({ queryKey: ['role-claims', roleId], enabled: Boolean(roleId), queryFn: () => fetchRoleClaims(roleId!) });
  // Mappa claimValue → id (per togglare on/off).
  const claimByValue = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of claimsQ.data?.data ?? []) if (c.claimValue) m.set(c.claimValue, c.id);
    return m;
  }, [claimsQ.data]);

  const toggle = useMutation({
    mutationFn: async ({ value, on, claimId }: { value: string; on: boolean; claimId?: string }) => {
      if (on) { await addRoleClaim(roleId!, 'custom', value); return; }
      /* Stessa guardia della scheda «Rotte»: vedi la spiegazione lì e in `fetchRoleClaims`. */
      if (!claimId) throw new Error('Permesso non trovato fra quelli del ruolo: ricarica la pagina e riprova.');
      await removeRoleClaim(claimId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['role-claims', roleId] }),
    onError: (e) => notifyError(e, 'Errore salvataggio permesso'),
  });

  const entities = useMemo(() => {
    const q = search.trim().toLowerCase();
    return CENSITED_ENTITIES
      .map((kebab) => ({ kebab, name: entityName(kebab) }))
      .filter((e) => !q || e.name.toLowerCase().includes(q) || e.kebab.includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [search]);

  /** Quanti permessi sono attivi per colonna, sulle sole entità **mostrate**. */
  const countByVerb = useMemo(() => {
    const m: Record<string, number> = {};
    for (const v of CRUD_VERBS) m[v.verb] = entities.filter((e) => claimByValue.has(`${e.name}.${v.verb}`)).length;
    return m;
  }, [entities, claimByValue]);

  /**
   * Accende o spegne un'intera colonna in un colpo — 91 entità, ed erano 91 click.
   *
   * Agisce sulle entità **mostrate**, quindi la ricerca restringe anche l'azione di massa:
   * è la stessa regola della scheda «Rotte», e permette di fare «tutte le letture delle
   * entità che contengono *erp*» senza toccare il resto.
   *
   * ⚠️ Il giro è sequenziale come quello delle rotte, ma qui le chiamate sono fino a 91:
   * una che fallisce a metà lascerebbe la colonna a metà strada. Gli errori si **raccolgono
   * e si riportano** invece di interrompere il giro, e l'elenco si rilegge comunque — così
   * quello che si vede è lo stato vero, non quello sperato.
   */
  const bulkVerb = useMutation({
    mutationFn: async ({ verb, on }: { verb: string; on: boolean }) => {
      let applied = 0;
      const failed: string[] = [];
      for (const e of entities) {
        const value = `${e.name}.${verb}`;
        const claimId = claimByValue.get(value);
        if (on === Boolean(claimId)) continue; // già nello stato richiesto
        try {
          if (on) await addRoleClaim(roleId!, 'custom', value);
          else await removeRoleClaim(claimId!);
          applied += 1;
        } catch { failed.push(e.name); }
      }
      return { applied, failed };
    },
    onSuccess: ({ applied: applicati, failed: falliti }, { verb, on }) => {
      qc.invalidateQueries({ queryKey: ['role-claims', roleId] });
      const column = CRUD_VERBS.find((v) => v.verb === verb)?.label ?? verb;
      if (falliti.length) {
        const first = falliti.slice(0, 5).join(', ');
        notifications.show({
          color: 'orange',
          title: `${column}: ${applicati} su ${applicati + falliti.length} applicati`,
          message: `Non riuscite: ${first}${falliti.length > 5 ? ` e altre ${falliti.length - 5}` : ''}.`,
        });
        return;
      }
      const noun = applicati === 1 ? 'permesso' : 'permessi';
      const participle = on
        ? (applicati === 1 ? 'abilitato' : 'abilitati')
        : (applicati === 1 ? 'disabilitato' : 'disabilitati');
      notifications.show({ color: 'teal', message: `${column}: ${applicati} ${noun} ${participle}.` });
    },
    onError: (e) => notifyError(e, 'Errore aggiornamento massivo'),
  });

  const busy = toggle.isPending || bulkVerb.isPending;

  return (
    <Stack gap="md">
      <Card withBorder radius="lg" padding="md">
        <Group justify="space-between" wrap="wrap" gap="sm" align="flex-end">
          <Select
            label="Ruolo"
            placeholder="Scegli un ruolo…"
            data={roles.map((r) => ({ value: r.id, label: r.name ?? r.id }))}
            value={roleId}
            onChange={setRoleId}
            searchable
            leftSection={<IconShieldCheck size={16} />}
            w={340}
          />
          {roleId && (
            <TextInput label="Cerca entità" placeholder="…" leftSection={<IconSearch size={16} />} value={search} onChange={(e) => setSearch(e.currentTarget.value)} w={260} />
          )}
        </Group>
      </Card>

      {!roleId && (
        <EmptyState icon={IconListCheck} title="Scegli un ruolo" description="Seleziona un ruolo nella select in alto per gestirne i permessi CRUD." py="xl" />
      )}

      {roleId && !editable && (
        <Alert color={role?.isFromMaster ? 'gray' : 'yellow'} variant="light" icon={<IconLock size={18} />}>
          {role?.isFromMaster
            ? 'Ruolo di sistema: permessi gestiti centralmente. Solo un amministratore può modificarli.'
            : 'Sola lettura: puoi modificare solo i ruoli di livello inferiore al tuo.'}
        </Alert>
      )}

      {roleId && (
        <Card withBorder radius="lg" padding={0}>
          {claimsQ.isLoading ? (
            <Group justify="center" py="xl"><Loader /></Group>
          ) : (
            <ScrollArea type="auto">
              <Table verticalSpacing="xs" horizontalSpacing="md" highlightOnHover striped="even" miw={680}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ minWidth: 240 }}>Entità</Table.Th>
                    {CRUD_VERBS.map((v) => {
                      const attivi = countByVerb[v.verb] ?? 0;
                      /* Tutte accese → il pulsante offre lo spegnimento, e viceversa: un solo
                         pulsante per colonna invece di due, che a 110px non ci starebbero. */
                      const all = entities.length > 0 && attivi === entities.length;
                      const inProgress = bulkVerb.isPending && bulkVerb.variables?.verb === v.verb;
                      return (
                        <Table.Th key={v.verb} ta="center" style={{ minWidth: 140 }}>
                          <Stack gap={4} align="center">
                            <Text size="sm" fw={700} c={v.color}>{v.label}</Text>
                            <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {attivi}/{entities.length}
                            </Text>
                            {editable && (
                              <Button
                                size="compact-xs"
                                variant="light"
                                color={all ? 'gray' : v.color}
                                leftSection={all ? <IconSquareOff size={13} /> : <IconChecks size={13} />}
                                loading={inProgress}
                                disabled={entities.length === 0 || (busy && !inProgress)}
                                onClick={() => bulkVerb.mutate({ verb: v.verb, on: !all })}
                              >
                                {all ? 'Disabilita tutte' : 'Abilita tutte'}
                              </Button>
                            )}
                          </Stack>
                        </Table.Th>
                      );
                    })}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {entities.map((e) => (
                    <Table.Tr key={e.kebab}>
                      <Table.Td><Text size="sm" fw={500}>{e.name}</Text></Table.Td>
                      {CRUD_VERBS.map((v) => {
                        const value = `${e.name}.${v.verb}`;
                        const has = claimByValue.has(value);
                        return (
                          <Table.Td key={v.verb} ta="center">
                            <Switch
                              checked={has}
                              color={v.color}
                              disabled={!editable || busy}
                              onChange={() => toggle.mutate({ value, on: !has, claimId: claimByValue.get(value) })}
                              aria-label={`${value} ${has ? 'attivo' : 'disattivo'}`}
                              styles={{ root: { display: 'inline-flex' } }}
                            />
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                  ))}
                  {entities.length === 0 && (
                    <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" ta="center" py="md">Nessuna entità trovata.</Text></Table.Td></Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          )}
        </Card>
      )}
    </Stack>
  );
}
